import { DialogButton } from '@guildeducationinc/recess';
import React, { createContext, useState, useContext } from 'react';

/**
 * Provider that keeps context of current Modal content and open || closed state.
 * This provider also exposes methods to open and close the modal via 'useModal' hook. Example:
 * ```jsx
 * import { useModal } from '@src/context/ModalContext';
 *
 * const { openModal, closeModal } = useModal();
 *
 * <Button onClick={() => openModal('Your modal string')}>Open Modal</Button>;
 * <Button onClick={() => openModal(<p>Your modal content<p>)}>Open Modal</Button>;
 * <Button onClick={() => openModal(<YourModalComponent {...props} />)}>Open Modal</Button>;
 *
 * <Button onClick={closeModal}>Close Modal</Button>
 * ```
 *
 * Modals can be opened with a second argument specifying options for the 'BaseModal'. Example:
 * ```
 * openModal(<ModalComponent, { closable: false, ...options });
 * ```
 * @packageDocumentation
 */

export interface ModalOptions {
  closable?: boolean;
  containerClass?: string;
  onModalClose?: () => any;
  tag?: string;
  /**
   * aria-labelledby identifier; typically points to a title or heading
   */
  labelledBy?: string;
  /**
   * aria-describedby identifier; typically points to a paragraph or other additional context
   */
  describedBy?: string;
  primaryButton?: DialogButton;
  secondaryButton?: DialogButton;
  title?: string;
  titleComponent?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const ModalContext = createContext<
  | {
      closeModal: () => void;
      openModal: (node: React.ReactNode, options?: ModalOptions) => void;
      modalContent: React.ReactNode;
      isModalOpen: boolean;
      modalOptions: ModalOptions;
      setModalContent: (node: React.ReactNode) => void;
      setModalOptions: (options: ModalOptions) => void;
    }
  | undefined
>(undefined);

const ModalProvider: React.FC = ({ children }) => {
  const defaultModalOptions = { closable: true };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);
  const [modalOptions, setModalOptions] = useState<ModalOptions>(defaultModalOptions);

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  const openModal = (node: React.ReactNode, options?: ModalOptions) => {
    setIsModalOpen(true);
    setModalContent(node);
    setModalOptions(options ?? defaultModalOptions);
  };

  return (
    <ModalContext.Provider
      value={{
        closeModal,
        openModal,
        modalContent,
        isModalOpen,
        modalOptions,
        setModalContent,
        setModalOptions,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

const useModal = () => {
  const context = useContext(ModalContext);

  /* istanbul ignore if */
  if (context === undefined) {
    throw new Error('useModal must be used within a ModalProvider.');
  }

  return context;
};

export default ModalProvider;

export { useModal };
